// apps/web/src/features/cargo/components/CargoImportModal.tsx
//
// Import modal (#1232) for admin / logist_cn only. Pick a format (yan /
// yan_full_truck) and one OR many workbooks, validate each (extension/size/
// name) BEFORE upload, presign + PUT every file, THEN process the batch via
// `POST /cargo/import` with the issued objectKeys + chosen format. The
// transient per-file result lists render below; they are never re-read as
// persisted counters.

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CARGO_IMPORT_FILE_FORMATS,
  CARGO_IMPORT_FILE_FORMAT_LABELS,
  CARGO_IMPORT_MAX_FILES,
  type CargoImportFileFormat,
  type CargoImportResponse,
} from '@magimp/shared';

import { Button, EditorModal, Select, type SelectOption } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { useImportCargo } from '../mutations.js';
import {
  CARGO_IMPORT_ACCEPT,
  uploadCargoImportFile,
  validateCargoImportFile,
  type CargoImportFileError,
} from '../forms/useCargoImportUpload.js';
import { CargoImportResults } from './CargoImportResults.js';

export function CargoImportModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['cargo', 'common']);
  const showToast = useToastStore((s) => s.show);
  const importMutation = useImportCargo();
  const inputRef = useRef<HTMLInputElement>(null);

  const [format, setFormat] = useState<CargoImportFileFormat>('yan');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CargoImportResponse | null>(null);

  const formatOptions = useMemo<SelectOption[]>(
    () =>
      CARGO_IMPORT_FILE_FORMATS.map((f) => ({
        value: f,
        label: CARGO_IMPORT_FILE_FORMAT_LABELS[f],
      })),
    [],
  );

  function importErrorText(error: CargoImportFileError): string {
    return t(`cargo:import.errors.${error}`);
  }

  function onPick(picked: FileList | null) {
    if (!picked) return;
    const accepted: File[] = [];
    for (const file of Array.from(picked)) {
      const v = validateCargoImportFile(file);
      if (!v.ok && v.error) {
        showToast({ tone: 'error', text: `${file.name}: ${importErrorText(v.error)}` });
        continue;
      }
      accepted.push(file);
    }
    // Enforce the batch cap BEFORE any upload so 21+ valid files never produce
    // orphaned uploaded objects from a server-rejected POST.
    const remaining = CARGO_IMPORT_MAX_FILES - files.length;
    if (accepted.length > remaining) {
      showToast({
        tone: 'error',
        text: t('cargo:import.errors.tooManyFiles', { max: CARGO_IMPORT_MAX_FILES }),
      });
    }
    const toAdd = accepted.slice(0, Math.max(0, remaining));
    if (toAdd.length > 0) setFiles((prev) => [...prev, ...toAdd]);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function onProcess() {
    if (files.length === 0 || files.length > CARGO_IMPORT_MAX_FILES) return;
    setBusy(true);
    setResult(null);
    try {
      // Upload ALL files first; only once every objectKey resolves do we fire
      // the single process call — this ordering is the contract under test.
      const objectKeys = await Promise.all(files.map((f) => uploadCargoImportFile(f)));
      const response = await importMutation.mutateAsync({
        files: objectKeys.map((objectKey) => ({ objectKey, format })),
      });
      setResult(response);
      setFiles([]);
      showToast({ tone: 'success', text: t('cargo:import.toasts.processed') });
    } catch (err) {
      showToast({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    // No text-field dirty tracking here (file selection only), so the
    // unsaved-changes guard is satisfied with `dirty={false}`; `busy` blocks
    // dismissal mid-upload.
    <EditorModal
      open
      onConfirmClose={onClose}
      dirty={false}
      busy={busy}
      title={t('cargo:import.title')}
      width={620}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('cargo:import.format')}</label>
          <div data-testid="cargo-import-format">
            <Select
              value={format}
              onChange={(v) => setFormat(v as CargoImportFileFormat)}
              options={formatOptions}
              style={{ width: '100%' }}
              disabled={busy}
            />
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept={CARGO_IMPORT_ACCEPT}
          disabled={busy}
          onChange={(e) => {
            onPick(e.currentTarget.files);
            e.currentTarget.value = '';
          }}
          style={{ display: 'none' }}
          data-testid="cargo-import-input"
        />
        <Button
          kind="secondary"
          icon="upload"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          data-testid="cargo-import-pick"
        >
          {t('cargo:import.pickFiles')}
        </Button>

        {files.length > 0 && (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
            data-testid="cargo-import-file-list"
          >
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--fg-1)',
                  border: '1px solid var(--border-1)',
                  borderRadius: 'var(--r-2)',
                  padding: '4px 8px',
                }}
              >
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {file.name}
                </span>
                <Button
                  size="sm"
                  kind="ghost"
                  icon="x"
                  aria-label={t('cargo:import.removeFile')}
                  disabled={busy}
                  onClick={() => removeFile(index)}
                />
              </li>
            ))}
          </ul>
        )}

        {result && <CargoImportResults files={result.files} />}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button kind="ghost" onClick={onClose} disabled={busy}>
            {t('common:actions.close')}
          </Button>
          <Button
            kind="primary"
            onClick={() => void onProcess()}
            disabled={busy || files.length === 0 || files.length > CARGO_IMPORT_MAX_FILES}
            data-testid="cargo-import-process"
          >
            {busy ? t('cargo:import.processing') : t('cargo:import.process')}
          </Button>
        </div>
      </div>
    </EditorModal>
  );
}
